import './polyfills';
import './utilities/cobranding/bootThemeBootstrap';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import Spinner from '@atlaskit/spinner';

import ErrorBoundary, { UnexpectedApplicationErrorPage } from './components/ErrorBoundary';

import {
  addBreadcrumb,
  captureException,
  initClient as initErrorReportingClient,
  setAnonymousIdTag,
  setUserId as setErrorReportingUserId,
  initSentry,
} from './utilities/analytics/error-reporting';
import LocaleSwitcher from './components/LocaleSwitcher';
import {
  buildInitialState,
  createAnalyticsClient,
  startupAnalyticEvent,
  pushGlobals,
  preloadAppState,
} from './utilities/init-app-state';
import { initCastle } from './castle';
import AnalyticsClientProvider from './containers/AnalyticsClientProvider';
import { createAppRouter } from './containers/App';
import { startBrowserMetrics } from './browser-metrics';
import { createAppStore } from './stores/redux.store';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FeatureGateProvider } from './providers/FeatureGateProvider';
import { AppRouterProvider } from './providers/AppRouterProvider';
import { getFfsId } from './utilities/ffs-id';
import AppProvider from './containers/AppProvider';
import FedrampUseIdProbe from './utilities/debug/FedrampUseIdProbe';
import { isFedM } from './utilities/env';
import { initPlatformFeatureFlagResolver } from './utilities/feature-flags/platform-feature-flags';
import { useFeatureGate } from '@atlassian/feature-gates-react';
import { AidFrontendFeatureGate } from './providers/FeatureGates';

const FeatureGatedAppRouter = () => {
  const use2StepStateEndpoint = useFeatureGate(AidFrontendFeatureGate.USE_2STEP_STATE_ENDPOINT);
  const router = React.useMemo(
    () => createAppRouter({ use2StepStateEndpoint }),
    [use2StepStateEndpoint]
  );

  return <AppRouterProvider router={router} />;
};

// Must happen before the first Atlaskit component renders (including the spinner below).
initPlatformFeatureFlagResolver();

// Render a spinner to remove the "failed to load scripts" error while loading the app
const container = document.getElementById('root');

const root = createRoot(container!);

root.render(
  <div style={{ display: 'flex', height: '100%', justifyContent: 'center', alignItems: 'center' }}>
    <Spinner size="large" />
  </div>
);

const preloadedState = preloadAppState();
initSentry();

buildInitialState(preloadedState)
  .then(async initialState => {
    const { initialStoreState, cspNonce } = initialState;

    initErrorReportingClient(initialStoreState.microbranding);

    const analyticsClient = createAnalyticsClient(initialState);

    const anonymousId = analyticsClient.getAnonymousId();
    setErrorReportingUserId(anonymousId);
    setAnonymousIdTag(anonymousId);

    startupAnalyticEvent(analyticsClient);

    startBrowserMetrics(analyticsClient);

    const ffsId = getFfsId();
    if (ffsId) {
      analyticsClient.setFfsId(ffsId);
    }

    pushGlobals(initialStoreState.appConfig, cspNonce);

    const store = createAppStore(initialStoreState);

    if (!initialStoreState.appConfig) {
      addBreadcrumb({
        level: 'error',
        message: 'Unable to retrieve appConfig during buildInitialState',
      });
    }

    initCastle(initialStoreState.appConfig);

    const queryClient = new QueryClient({ defaultOptions: { queries: { retry: false } } });

    root.render(
      <Provider store={store}>
        <LocaleSwitcher>
          <ErrorBoundary analyticsClient={analyticsClient}>
            <AppProvider>
              <QueryClientProvider client={queryClient}>
                <AnalyticsClientProvider>
                  <FeatureGateProvider>
                    {isFedM() && <FedrampUseIdProbe context="root" />}
                    <FeatureGatedAppRouter />
                  </FeatureGateProvider>
                </AnalyticsClientProvider>
              </QueryClientProvider>
            </AppProvider>
          </ErrorBoundary>
        </LocaleSwitcher>
      </Provider>
    );
  })
  .catch(error => {
    captureException(error);

    const emptyStore = createAppStore({});
    root.render(
      <Provider store={emptyStore}>
        <LocaleSwitcher>
          <UnexpectedApplicationErrorPage />
        </LocaleSwitcher>
      </Provider>
    );
  });
